"use client";

import * as React from "react";
import {
  AdminPortalAuditLogStreamingError,
  AdminPortalAuditLogStreamingLoading,
  AdminPortalAuditLogStreaming as AdminPortalAuditLogStreamingPresentational,
  AdminPortalAuditLogStreamingButton,
} from "./lib/admin-portal-audit-log-streaming.js";
import type { AdminPortalAuditLogStreamingStatusProps } from "./lib/admin-portal-audit-log-streaming.js";
import { ApiProvider, AuthToken, useApiReady } from "./api/api-provider.js";
import {
  useGenerateAdminPortalLink,
  useGetAuditLogStream,
  type AuditLogStreamJSON,
} from "./api/endpoint.js";
import { useWorkOsApiUrl } from "./lib/widgets-context.js";
import { ErrorBoundary } from "./lib/error-boundary.js";
import { WidgetRootDomProps } from "./lib/utils.js";

interface AdminPortalAuditLogStreamingProps extends WidgetRootDomProps {
  authToken: AuthToken;
}

const AdminPortalAuditLogStreaming: React.FC<
  AdminPortalAuditLogStreamingProps
> = ({ authToken, ...domProps }) => {
  const baseUrl = useWorkOsApiUrl();
  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <AdminPortalAuditLogStreamingError error={error} {...domProps} />
      )}
    >
      <ApiProvider
        widgetType="admin-portal-audit-log-streaming"
        authToken={authToken}
        baseUrl={baseUrl}
      >
        <AdminPortalAuditLogStreamingImpl {...domProps} />
      </ApiProvider>
    </ErrorBoundary>
  );
};

const AdminPortalAuditLogStreamingImpl = (props: WidgetRootDomProps) => {
  const isApiReady = useApiReady();

  const { mutate: generateAdminPortalLink, ...mutation } =
    useGenerateAdminPortalLink({
      mutation: {
        onSuccess: (data) => {
          window.open(data.link, "_blank", "noopener,noreferrer");
        },
      },
    });

  const adminPortalLink = mutation.data?.link ?? null;

  const query = useGetAuditLogStream({
    query: {
      enabled: isApiReady,
    },
  });

  const initConfig = () => {
    generateAdminPortalLink({
      params: {
        intent: "log_streams",
      },
    });
  };

  if (query.isLoading) {
    return <AdminPortalAuditLogStreamingLoading {...props} />;
  }

  if (query.isError) {
    return <AdminPortalAuditLogStreamingError error={query.error} {...props} />;
  }

  if (query.data) {
    const stream = query.data;

    // Check if stream is configured (has id property)
    // API returns empty object {} when no stream is configured
    if (!Object.hasOwn(stream, "id")) {
      return (
        <AdminPortalAuditLogStreamingPresentational
          {...props}
          connectionStatus="NotConfigured"
          adminPortalOpenButton={
            <AdminPortalAuditLogStreamingButton
              isPending={mutation.isPending}
              href={adminPortalLink}
              initConfig={initConfig}
            />
          }
        />
      );
    }

    // Type assertion: we've confirmed stream has id, so it's AuditLogStreamJSON
    const configuredStream = stream as AuditLogStreamJSON;

    // Map API state to widget status
    const statusProps: AdminPortalAuditLogStreamingStatusProps = (() => {
      switch (configuredStream.state) {
        case "Inactive":
          return {
            connectionStatus: "Inactive",
            destinationType: configuredStream.type,
          };
        case "Active":
          return {
            connectionStatus: "Active",
            destinationType: configuredStream.type,
            lastSyncedEventId: configuredStream.lastSyncedEventId,
          };
        case "Error":
          return {
            connectionStatus: "Error",
            destinationType: configuredStream.type,
            lastSyncedEventId: configuredStream.lastSyncedEventId,
          };
        case "Invalid":
          return {
            connectionStatus: "Error",
            destinationType: configuredStream.type,
            lastSyncedEventId: configuredStream.lastSyncedEventId,
          };
        default:
          // Fallback
          return {
            connectionStatus: "Inactive",
            destinationType: configuredStream.type,
          };
      }
    })();

    return (
      <ErrorBoundary
        fallbackRender={({ error }) => (
          <AdminPortalAuditLogStreamingError error={error} {...props} />
        )}
      >
        <AdminPortalAuditLogStreamingPresentational
          {...props}
          {...statusProps}
          adminPortalOpenButton={
            <AdminPortalAuditLogStreamingButton
              isPending={mutation.isPending}
              href={adminPortalLink}
              initConfig={initConfig}
            />
          }
        />
      </ErrorBoundary>
    );
  }

  return <AdminPortalAuditLogStreamingLoading {...props} />;
};

export { AdminPortalAuditLogStreaming };
export type { AdminPortalAuditLogStreamingProps };
export {
  AdminPortalAuditLogStreamingLoading,
  AdminPortalAuditLogStreamingError,
} from "./lib/admin-portal-audit-log-streaming.js";
export {
  AdminPortalAuditLogStreaming as AdminPortalAuditLogStreamingPresentational,
  AdminPortalAuditLogStreamingButton,
  type AdminPortalAuditLogStreamingStatusProps,
} from "./lib/admin-portal-audit-log-streaming.js";
